import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [services, setServices] = useState([]);
  const [isHovered, setIsHovered] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();
  const hoverTimeoutRef = useRef(null);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setMobileServicesOpen(false);
    setIsHovered(false);
  }, [location]);

  // Fetch services dynamically from CMS
  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/ydelser')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          // Sort services alphabetically by title
          const sorted = [...data].sort((a, b) => a.title.localeCompare(b.title, 'da'));
          setServices(sorted);
        }
      })
      .catch((err) => console.error('Error fetching services for header:', err));
  }, []);

  // Handle hover with a small delay to prevent accidental closing
  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 150); // 150ms delay
  };

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-brand-navy-100 bg-white text-brand-navy-900 shadow-sm">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=200,quality=90,format=auto/assets/af349c16-3f04-4dd3-a862-642e24736c63/entreprenoer-jan-noergaard-logo.png"
            alt="Entreprenør Jan Nørgaard ApS firmalogo - Autoriseret Kloakmester & Entreprenør"
            title="Entreprenør Jan Nørgaard ApS"
            className="h-8 w-auto object-contain transition-transform duration-200 group-hover:scale-102"
          />
        </Link>

        {/* Desktop Navigation (Visible at >= 1200px) */}
        <nav className="hidden min-0:hidden min-[1200px]:flex items-center gap-8">
          <NavLink
            to="/"
            className={({ isActive }) =>
              `font-display text-sm font-bold uppercase tracking-wider transition-colors hover:text-brand-orange-500 ${
                isActive ? 'text-brand-orange-500' : 'text-brand-navy-900'
              }`
            }
          >
            Forside
          </NavLink>

          {/* Services Dropdown Trigger */}
          <div
            className="relative"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <button
              type="button"
              className={`flex items-center gap-1.5 font-display text-sm font-bold uppercase tracking-wider transition-colors hover:text-brand-orange-500 cursor-pointer ${
                location.pathname.startsWith('/ydelser/') || isHovered ? 'text-brand-orange-500' : 'text-brand-navy-900'
              }`}
            >
              Ydelser
              <i className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${isHovered ? 'rotate-180' : ''}`} />
            </button>

            {/* Mega Dropdown Panel - Always in DOM for accessibility & SEO */}
            {services.length > 0 && (
              <div 
                className={`absolute left-1/2 -translate-x-1/2 mt-4 w-[680px] rounded-xl border border-brand-navy-100 bg-white p-6 shadow-2xl z-50 transition-all duration-200 ${
                  isHovered 
                    ? 'opacity-100 translate-y-0 pointer-events-auto' 
                    : 'opacity-0 -translate-y-2 pointer-events-none'
                }`}
                aria-label="Undermenu for ydelser"
              >
                <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                  {services.map((service) => (
                    <Link
                      key={service.id}
                      to={`/ydelser/${service.slug.da}`}
                      className="block py-1 text-sm font-bold text-brand-navy-700 hover:text-brand-orange-500 transition-colors truncate"
                    >
                      {service.title}
                    </Link>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-brand-navy-100 text-center">
                  <span className="text-xs text-brand-navy-500 font-medium">
                    Autoriseret kloakmester & entreprenør
                  </span>
                </div>
              </div>
            )}
          </div>

          <NavLink
            to="/om-os"
            className={({ isActive }) =>
              `font-display text-sm font-bold uppercase tracking-wider transition-colors hover:text-brand-orange-500 ${
                isActive ? 'text-brand-orange-500' : 'text-brand-navy-900'
              }`
            }
          >
            Om os
          </NavLink>

          <NavLink
            to="/referencer"
            className={({ isActive }) =>
              `font-display text-sm font-bold uppercase tracking-wider transition-colors hover:text-brand-orange-500 ${
                isActive ? 'text-brand-orange-500' : 'text-brand-navy-900'
              }`
            }
          >
            Referencer
          </NavLink>

          <NavLink
            to="/kontakt"
            className={({ isActive }) =>
              `font-display text-sm font-bold uppercase tracking-wider transition-colors hover:text-brand-orange-500 ${
                isActive ? 'text-brand-orange-500' : 'text-brand-navy-900'
              }`
            }
          >
            Kontakt
          </NavLink>
        </nav>

        {/* Call to Action Button (Desktop) */}
        <div className="hidden min-[1200px]:block">
          <Link
            to="/kontakt"
            className="inline-flex items-center gap-2 rounded bg-brand-orange-500 px-5 py-2.5 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-brand-orange-600"
          >
            <i className="fa-solid fa-phone" /> Ring op
          </Link>
        </div>

        {/* Mobile Menu Button (Visible < 1200px) */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex h-10 w-10 items-center justify-center text-brand-navy-900 hover:text-brand-orange-500 transition-colors min-[1200px]:hidden"
          aria-label="Toggle menu"
        >
          <i className={`fa-solid ${isOpen ? 'fa-xmark' : 'fa-bars'} text-lg`} />
        </button>
      </div>

      {/* Mobile Navigation (Visible < 1200px) */}
      {isOpen && (
        <div className="border-t border-brand-navy-100 bg-white px-4 py-4 min-[1200px]:hidden shadow-lg">
          <nav className="flex flex-col gap-4">
            <NavLink
              to="/"
              className={({ isActive }) =>
                `block rounded px-3 py-2 text-base font-bold uppercase tracking-wider transition-colors ${
                  isActive ? 'bg-brand-orange-500 text-white' : 'text-brand-navy-900 hover:bg-brand-navy-50'
                }`
              }
            >
              Forside
            </NavLink>

            {/* Mobile Services Accordion */}
            <div className="flex flex-col">
              <button
                onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                className={`flex w-full items-center justify-between rounded px-3 py-2 text-base font-bold uppercase tracking-wider transition-colors ${
                  location.pathname.startsWith('/ydelser/') ? 'text-brand-orange-500' : 'text-brand-navy-900 hover:bg-brand-navy-50'
                }`}
              >
                <span>Ydelser</span>
                <i className={`fa-solid fa-chevron-${mobileServicesOpen ? 'up' : 'down'} text-sm text-brand-orange-500`} />
              </button>
              {mobileServicesOpen && (
                <div className="ml-4 mt-1 flex flex-col gap-2 border-l border-brand-navy-100 pl-3 max-h-[320px] overflow-y-auto">
                  {services.map((service) => (
                    <Link
                      key={service.id}
                      to={`/ydelser/${service.slug.da}`}
                      className="text-sm text-brand-navy-700 hover:text-brand-orange-500 py-1.5 font-medium"
                    >
                      {service.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <NavLink
              to="/om-os"
              className={({ isActive }) =>
                `block rounded px-3 py-2 text-base font-bold uppercase tracking-wider transition-colors ${
                  isActive ? 'bg-brand-orange-500 text-white' : 'text-brand-navy-900 hover:bg-brand-navy-50'
                }`
              }
            >
              Om os
            </NavLink>

            <NavLink
              to="/referencer"
              className={({ isActive }) =>
                `block rounded px-3 py-2 text-base font-bold uppercase tracking-wider transition-colors ${
                  isActive ? 'bg-brand-orange-500 text-white' : 'text-brand-navy-900 hover:bg-brand-navy-50'
                }`
              }
            >
              Referencer
            </NavLink>

            <NavLink
              to="/kontakt"
              className={({ isActive }) =>
                `block rounded px-3 py-2 text-base font-bold uppercase tracking-wider transition-colors ${
                  isActive ? 'bg-brand-orange-500 text-white' : 'text-brand-navy-900 hover:bg-brand-navy-50'
                }`
              }
            >
              Kontakt
            </NavLink>

            <div className="mt-4 border-t border-brand-navy-100 pt-4">
              <a
                href="tel:+4520483818"
                className="flex items-center justify-center gap-2 rounded bg-brand-orange-500 py-3 font-display text-base font-bold uppercase tracking-wider text-white transition-colors hover:bg-brand-orange-600"
              >
                <i className="fa-solid fa-phone" /> Ring til Jan: 20 48 38 18
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
